import ContentSaveIcon from '@mui/icons-material/Save';
import { Button as MuiButton, Stack } from '@mui/material';
import { FormEvent, useEffect } from 'react';
import {
    RecordContextProvider,
    Toolbar,
    useCreate,
    useDelete,
    useNotify,
    useUpdate,
} from 'react-admin';
import { FormProvider, useForm } from 'react-hook-form';
import type { ExpensesClaimInput } from '@domain/expensesClaim';
import type { ExpensesClaimSummary } from '@wwf/sdk';
import { ExpensesClaimInputs } from './ExpensesClaimInputs';

type DrawerMode = 'create' | 'edit' | 'show';

const defaultValues: ExpensesClaimInput = {
    name: '',
    details: '',
    appScheme: '',
    url: null,
    docUrl: null,
};

export function ExpensesClaimDrawerForm({
    mode,
    record,
    loading,
    onClose,
    onSuccess,
}: {
    mode: DrawerMode;
    record?: ExpensesClaimSummary;
    loading: boolean;
    onClose(): void;
    onSuccess(): void;
}) {
    const notify = useNotify();
    const [create] = useCreate();
    const [update] = useUpdate();
    const [deleteOne] = useDelete();

    const form = useForm<ExpensesClaimInput>({
        defaultValues,
    });

    useEffect(() => {
        if ((mode === 'edit' || mode === 'show') && record) {
            form.reset({
                name: record.name ?? '',
                details: record.details ?? '',
                appScheme: record.appScheme ?? '',
                url: record.url,
                docUrl: record.docUrl,
            });
        } else {
            form.reset(defaultValues);
        }
    }, [mode, record, form]);

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (mode === 'show') return;
        form.handleSubmit(async (data: ExpensesClaimInput) => {
            try {
                if (mode === 'create') {
                    await create(
                        'expensesClaim',
                        {
                            data: {
                                name: data.name,
                                details: data.details ?? null,
                                appScheme: data.appScheme ?? null,
                                url: data.url ?? null,
                                docUrl: data.docUrl ?? null,
                            },
                            meta: {},
                        },
                        {
                            onSuccess: () => {
                                onSuccess();
                                onClose();
                            },
                        }
                    );
                } else {
                    if (record?.id == null) {
                        notify('Record not found', { type: 'error' });
                        return;
                    }
                    await update(
                        'expensesClaim',
                        {
                            id: record.id,
                            data: {
                                name: data.name,
                                details: data.details ?? null,
                                appScheme: data.appScheme ?? null,
                                url: data.url ?? null,
                                docUrl: data.docUrl ?? null,
                            },
                            previousData: record as any,
                            meta: {},
                        },
                        {
                            onSuccess: () => {
                                onSuccess();
                                onClose();
                            },
                        }
                    );
                }
            } catch (e) {
                notify(String(e), { type: 'error' });
            }
        })(event);
    };

    const handleDelete = () => {
        if (record?.id == null) return;
        deleteOne(
            'expensesClaim',
            { id: record.id, previousData: record as any },
            {
                onSuccess: () => {
                    onSuccess();
                    onClose();
                },
            }
        );
    };

    const saving = form.formState.isSubmitting;
    const canSave = !loading && !saving;

    return (
        <FormProvider {...form}>
            <RecordContextProvider value={record ?? undefined}>
                <form onSubmit={handleSubmit}>
                    <Stack gap={2}>
                        <ExpensesClaimInputs readOnly={mode === 'show'} />
                    </Stack>
                    {mode !== 'show' && (
                        <Toolbar
                            sx={{
                                mt: 2,
                                px: 0,
                                justifyContent: 'space-between',
                            }}
                        >
                            <Stack direction="row" gap={1}>
                                <MuiButton
                                    variant="contained"
                                    type="submit"
                                    disabled={!canSave}
                                    startIcon={<ContentSaveIcon />}
                                >
                                    Save
                                </MuiButton>
                                <MuiButton
                                    variant="text"
                                    onClick={onClose}
                                    disabled={saving}
                                >
                                    Cancel
                                </MuiButton>
                            </Stack>
                            {mode === 'edit' && record?.id != null && (
                                <MuiButton
                                    variant="text"
                                    color="error"
                                    disabled={saving}
                                    onClick={() => {
                                        if (
                                            window.confirm(
                                                'Are you sure you want to delete this expenses claim?'
                                            )
                                        ) {
                                            handleDelete();
                                        }
                                    }}
                                >
                                    Delete
                                </MuiButton>
                            )}
                        </Toolbar>
                    )}
                </form>
            </RecordContextProvider>
        </FormProvider>
    );
}
